<script lang="ts">
  import { api, ApiError } from '../lib/api';
  import { session } from '../lib/session.svelte';
  import type { FeedbackEntry, FeedbackKind } from '../../../shared/types';

  interface Props {
    onDone: () => void;
  }
  let { onDone }: Props = $props();

  // --- Submit form (any logged-in user) ------------------------------------
  let kind: FeedbackKind = $state('feature');
  let body = $state('');
  let busy = $state(false);
  let error: string | null = $state(null);
  let sent = $state(false);

  async function submit(e: SubmitEvent) {
    e.preventDefault();
    error = null;
    sent = false;
    if (!body.trim()) {
      error = 'Skriv noe i meldingen.';
      return;
    }
    busy = true;
    try {
      await api.submitFeedback({ kind, body: body.trim() });
      body = '';
      sent = true;
      // If we're a moderator, refresh the list so the new entry shows.
      if (session.user?.is_moderator) await refreshList();
    } catch (err) {
      error = err instanceof ApiError ? `Sending feilet (${err.status}).` : 'Sending feilet.';
    } finally {
      busy = false;
    }
  }

  // --- Moderator-only list -------------------------------------------------
  let entries: FeedbackEntry[] = $state([]);
  let listError: string | null = $state(null);
  let listLoading = $state(false);

  async function refreshList() {
    if (!session.user?.is_moderator) return;
    listLoading = true;
    listError = null;
    try {
      entries = await api.listFeedback();
    } catch (err) {
      listError = err instanceof ApiError && err.status === 403
        ? 'Bare moderatorer kan se listen.'
        : 'Kunne ikke laste tilbakemeldinger.';
    } finally {
      listLoading = false;
    }
  }

  // Auto-load for moderators (and admins, who imply moderator).
  $effect(() => {
    if (session.user?.is_moderator || session.user?.is_admin) refreshList();
  });

  async function toggleDone(entry: FeedbackEntry, done: boolean) {
    try {
      const updated = await api.updateFeedback(entry.id, { done });
      entries = entries.map((e) => (e.id === updated.id ? updated : e));
    } catch (err) {
      listError = err instanceof ApiError && err.status === 403
        ? 'Bare administratorer kan markere som ferdig.'
        : 'Endring feilet.';
    }
  }

  async function removeEntry(entry: FeedbackEntry) {
    if (!confirm('Slett denne tilbakemeldingen?')) return;
    try {
      await api.deleteFeedback(entry.id);
      entries = entries.filter((e) => e.id !== entry.id);
    } catch (err) {
      listError = err instanceof ApiError && err.status === 403
        ? 'Bare administratorer kan slette.'
        : 'Sletting feilet.';
    }
  }

  function formatDate(epochMs: number): string {
    return new Date(epochMs).toLocaleString('nb-NO', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    });
  }
</script>

<section aria-label="Tilbakemelding">
  <div class="row" style="justify-content: space-between; margin-bottom: 1rem;">
    <h2 style="margin: 0;">Tilbakemelding</h2>
    <button type="button" onclick={onDone}>Tilbake</button>
  </div>

  <form onsubmit={submit} class="card" aria-labelledby="fb-h">
    <h3 id="fb-h">Send inn</h3>
    <p class="muted">Forslag eller feilrapporter går til moderator. Sendingen er ikke anonym — vi ser hvem som sender.</p>

    <label for="fb-kind">Type</label>
    <select id="fb-kind" bind:value={kind}>
      <option value="feature">Forslag</option>
      <option value="bug">Feilrapport</option>
    </select>

    <label for="fb-body">Beskrivelse</label>
    <textarea id="fb-body" bind:value={body} rows="5"
              placeholder="Beskriv kort hva du tenker eller hva som gikk galt"
              required maxlength="4000"></textarea>

    {#if error}<p class="error" role="alert">{error}</p>{/if}
    {#if sent}<p class="muted" role="status">Takk, vi har mottatt det.</p>{/if}

    <div class="row" style="margin-top: 0.75rem;">
      <button class="primary" type="submit" disabled={busy}>
        {busy ? 'Sender …' : 'Send'}
      </button>
    </div>
  </form>

  {#if session.user?.is_moderator}
    <section class="card" aria-labelledby="fb-list-h">
      <div class="row" style="justify-content: space-between;">
        <h3 id="fb-list-h" style="margin: 0;">Innkommet ({entries.length})</h3>
        <button type="button" onclick={refreshList} disabled={listLoading}>
          {listLoading ? 'Laster …' : 'Oppdater'}
        </button>
      </div>
      {#if listError}<p class="error">{listError}</p>{/if}
      {#if !entries.length && !listLoading && !listError}
        <p class="muted">Ingen tilbakemeldinger ennå.</p>
      {/if}
      {#each entries as e (e.id)}
        <article class="card" style="margin-top: 0.5rem; {e.done_at ? 'opacity: 0.7;' : ''}">
          <div class="row" style="justify-content: space-between;">
            <div class="row" style="gap: 0.5rem;">
              <strong>{e.kind === 'feature' ? 'Forslag' : 'Feilrapport'}</strong>
              {#if e.done_at}
                <span class="vis-badge semi" style="background: rgba(46,160,67,0.18); color: #2ea043;">Ferdig</span>
              {:else}
                <span class="vis-badge semi">Åpen</span>
              {/if}
            </div>
            <span class="muted">{formatDate(e.created_at)}</span>
          </div>
          <p style="white-space: pre-wrap; margin: 0.5rem 0;">{e.body}</p>
          <p class="muted" style="font-size: 0.85rem;">
            Fra {e.user_display?.trim() || e.user_email}
          </p>
          {#if session.user?.is_admin}
            <div class="row" style="margin-top: 0.5rem;">
              {#if e.done_at}
                <button type="button" onclick={() => toggleDone(e, false)}>Marker som åpen</button>
              {:else}
                <button class="primary" type="button" onclick={() => toggleDone(e, true)}>Marker som ferdig</button>
              {/if}
              <button class="danger" type="button" onclick={() => removeEntry(e)}>Slett</button>
            </div>
          {/if}
        </article>
      {/each}
    </section>
  {/if}
</section>
